// @file Network requests related to things on the padlet page
/* eslint-disable no-return-await, no-template-curly-in-string */
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { transformCurrentUrl } from '@@/bits/location'
import type {
  Comment as CommentType,
  CommentsFetchQuery,
  JsonAPIResource,
  JsonAPIResponse,
  Reaction as ReactionType,
  ReactionsFetchQuery,
  User as UserType,
  Wall as WallType,
  WallBackgroundType,
  Wish as WishType,
  WishesFetchQuery,
} from '@padlet/arvo'
import { fetchComments, fetchReactions, fetchUser, fetchWishes } from '@padlet/arvo'
import type { FetchOptions } from '@padlet/fetch'
import { fetchResponse, HTTPMethod } from '@padlet/fetch'
import getCsrfToken from 'bits/csrf_token'
import type { WallAccessRight } from 'enums'
import { template } from 'lodash-es'
import { fetchJson, fetchJsonWithRetries } from 'surface/api_fetch'
import type {
  AccumulatedReactions,
  CanSetWallPrivacyOptions,
  DraftPost,
  GoogleDriveAuthorizationResult,
  Id,
  LibraryId,
  MagicWallCreationResult,
  Plan as PlanType,
  PrivacyPolicyId,
  UserGroupWallCollaborator,
  UserId,
  UserLibrarySettingsApiResponse,
  UserMentionSuggestion,
  UserMentionSuggestionAttributes,
  UserWallCollaborator,
  WallBackground,
  WallCreationFromTemplateResult,
  WallId,
  WallPostProperties as WallPostPropertiesType,
  WallReadTokenResponse,
  WallSectionBreakoutLink,
  WallSubmissionRequestLink,
  WallSubmissionRequestProps,
} from 'types'

export interface VersionedFetchOptions extends FetchOptions {
  apiVersion?: number
}

const API_VERSION = 5
const VIA = 'webapp'

class FetchableObject {
  public static get url(): string {
    return ''
  }

  public static get fetchUrl(): string {
    return ''
  }

  public static buildUrl(url, options): string {
    return template(url)({ ...options, interpolate: /\${([\s\S]+?)}/g })
  }

  public static async readAll(options, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.fetchUrl, options), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async read(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }

  public static async create(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }

  public static async delete(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class Poll extends FetchableObject {
  private static get updatePollUrl(): string {
    return `/api/\${ apiVersion }/polls/\${ pollId }`
  }

  private static get pollVoteUrl(): string {
    return `/api/\${ apiVersion }/polls/\${ pollId }/vote`
  }

  public static async updatePoll(
    pollParams: Record<string, any>,
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<Wish>> {
    const { apiVersion, ...otherFetchOptions } = fetchOptions
    const response = await fetchJson(this.buildUrl(this.updatePollUrl, { apiVersion, pollId: pollParams.poll_id }), {
      ...otherFetchOptions,
      method: HTTPMethod.patch,
      body: asciiSafeStringify(pollParams),
    })
    const updatedWish = (response.data as JsonAPIResource<Wish>).attributes
    return updatedWish
  }

  public static async vote(
    { pollId, wishId, pollChoiceIds }: { pollId: Id; wishId: Id; pollChoiceIds: Id[] },
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<Wish>> {
    const { apiVersion, ...otherFetchOptions } = fetchOptions
    const response = await fetchJson(this.buildUrl(this.pollVoteUrl, { apiVersion, pollId }), {
      ...otherFetchOptions,
      method: HTTPMethod.post,
      body: asciiSafeStringify({ wish_id: wishId, poll_choice_ids: pollChoiceIds }),
    })
    const updatedWish = (response.data as JsonAPIResource<Wish>).attributes
    return updatedWish
  }
}

class Reaction extends FetchableObject {
  public static async fetchAccumulatedReactions({
    wallId,
  }: {
    wallId: number
  }): Promise<JsonAPIResponse<AccumulatedReactions>> {
    return await fetchJson(`/api/${API_VERSION}/accumulated_reactions?wall_id=${wallId}`, {
      method: HTTPMethod.get,
    })
  }

  public static get url(): string {
    return `/api/${API_VERSION}/reactions`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/reactions?wall_id=\${ wall.id }`
  }

  public static async fetch(query: ReactionsFetchQuery): Promise<JsonAPIResponse<ReactionType>> {
    return await fetchReactions(query)
  }
}

class PostConnection extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wish_connections`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wish_connections?wall_id=\${ wall.id }`
  }
}

class Comment extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/comments`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/comments?wall_id=\${ wall.id }`
  }

  public static async fetch(query: CommentsFetchQuery): Promise<JsonAPIResponse<CommentType>> {
    return await fetchComments(query)
  }
}

class CommentV2 extends FetchableObject {
  public static get url(): string {
    return `/api/\${ apiVersion }/comments`
  }

  private static get updateUrl(): string {
    return `/api/\${ apiVersion }/comments/\${ commentId }`
  }

  public static async create(attributes: Partial<CommentType>, fetchOptions: VersionedFetchOptions = {}): Promise<any> {
    const { apiVersion = 7, ...otherFetchOptions } = fetchOptions
    return await fetchJson(this.buildUrl(this.url, { apiVersion }), {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ attributes }),
      ...otherFetchOptions,
    })
  }

  public static async update(
    commentId: Id,
    attributes: Partial<CommentType>,
    fetchOptions: VersionedFetchOptions = {},
  ): Promise<any> {
    const { apiVersion = 7, ...otherFetchOptions } = fetchOptions
    return await fetchJson(this.buildUrl(this.updateUrl, { apiVersion, commentId }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({ attributes }),
      ...otherFetchOptions,
    })
  }
}

class WallSection extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wall_sections`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wall_sections?wall_id=\${ wall.id }`
  }
}

// TODO: move to @padlet/arvo
class WallTableLayout extends FetchableObject {
  private static readonly rowsEndpoint = '/api/wall_table_layout/rows'
  private static readonly colsEndpoint = '/api/wall_table_layout/cols'
  private static readonly rowAndColEndpoint = '/api/wall_table_layout/row_and_col'

  public static get fetchUrl(): string {
    return '/api/wall_table_layout?wall_id=${ wall.id }'
  }

  public static async createRow(obj): Promise<any> {
    return await fetchJson(this.rowsEndpoint, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }

  public static async createCol(obj): Promise<any> {
    return await fetchJson(this.colsEndpoint, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }

  public static async createRowAndCol(obj): Promise<any> {
    return await fetchJson(this.rowAndColEndpoint, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }

  public static async updateRow(obj): Promise<any> {
    return await fetchJson(`${this.rowsEndpoint}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
    })
  }

  public static async updateCol(obj): Promise<any> {
    return await fetchJson(`${this.colsEndpoint}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
    })
  }

  public static async deleteRow(obj): Promise<any> {
    return await fetchJson(`${this.rowsEndpoint}/${obj.id}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async deleteCol(obj): Promise<any> {
    return await fetchJson(`${this.colsEndpoint}/${obj.id}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async putPostInCell(obj): Promise<any> {
    return await fetchJson('/api/wall_table_layout/cell', {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }
}

class Wall extends FetchableObject {
  public static get url(): string {
    return '/walls'
  }

  private static get convertLayoutUrl(): string {
    return '/walls/${ id }/layout'
  }

  public static async convertLayout({ id, newLayout }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.convertLayoutUrl, { id }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({ type: newLayout }),
      ...fetchOptions,
    })
  }

  public static async create(
    { viz, libraryId, groupBySection }: { viz: string; libraryId?: LibraryId; groupBySection?: boolean },
    fetchOptions = {},
  ): Promise<any> {
    const wishArrangement = groupBySection === true ? { wish_arrangement: { group_by: 'section' } } : {}
    return await fetchJson(`/api/${API_VERSION}/walls`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ viz, via: VIA, library_id: libraryId, ...wishArrangement }),
      ...fetchOptions,
    })
  }

  public static async createMagic(
    {
      role,
      userPrompt,
      currentLibraryId,
      isExample,
      modelName,
      includeImages,
    }: {
      role: string
      userPrompt: string
      currentLibraryId: LibraryId | undefined
      isExample: boolean
      modelName: string
      includeImages: boolean
    },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<MagicWallCreationResult>> {
    return await fetchJson(`/api/${API_VERSION}/walls/magic_wall`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        role,
        user_prompt: userPrompt,
        library_id: currentLibraryId,
        is_example: isExample,
        model: modelName,
        include_images: includeImages,
      }),
      ...fetchOptions,
    })
  }

  public static async archive(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/archive`, {
      method: HTTPMethod.put,
    })
  }

  public static async markAsTemplate(
    wallId: number,
    template: boolean,
  ): Promise<JsonAPIResponse<{ template: boolean }>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/mark-as-template`, {
      method: HTTPMethod.patch,
      body: JSON.stringify({ data: { attributes: { template } } }),
    })
  }

  public static async createFromTemplate(
    wallId: number,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<WallCreationFromTemplateResult>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/create-from-template`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }
}

class WallAccessSettings extends FetchableObject {
  public static async fetch(wallId: WallId): Promise<JsonAPIResponse<WallAccessSettings>> {
    return await fetchJson(`/api/6/walls/${wallId}/access_settings`, {
      method: HTTPMethod.get,
    })
  }

  public static async update(
    wallId: WallId,
    accessSettings: Partial<WallAccessSettings>,
  ): Promise<JsonAPIResponse<WallAccessSettings>> {
    return await fetchJson(`/api/6/walls/${wallId}/access_settings`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ data: { attributes: accessSettings } }),
    })
  }
}

class Wish extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wishes`
  }

  public static get fetchSingleUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wishes?wall_id=\${ wall.id }&page_start=\${ pageStart }`
  }

  public static get transferUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/transfer`
  }

  public static get copyUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/copy`
  }

  public static get approveUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/approve`
  }

  public static get rejectUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/reject`
  }

  public static async fetchSingle({ wishId }, fetchOptions = {}): Promise<JsonAPIResponse<WishType>> {
    return await fetchJson(this.buildUrl(this.fetchSingleUrl, { wish_id: wishId }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetch(query: WishesFetchQuery): Promise<JsonAPIResponse<WishType>> {
    return await fetchWishes(query)
  }

  public static async transfer(
    { wishId, wallId, wallSectionId, wallTitle, wallDescription, libraryId },
    fetchOptions = {},
  ): Promise<any> {
    return await fetchJson(this.buildUrl(this.transferUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({
        wall_id: wallId,
        wall_section_id: wallSectionId,
        wall_title: wallTitle,
        wall_description: wallDescription,
        library_id: libraryId,
      }),
      ...fetchOptions,
    })
  }

  public static async copy(
    { wishId, wallId, wallSectionId, wallTitle, wallDescription, postPosition, libraryId },
    fetchOptions = {},
  ): Promise<any> {
    return await fetchJson(this.buildUrl(this.copyUrl, { wish_id: wishId }), {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        wall_id: wallId,
        wall_section_id: wallSectionId,
        wall_title: wallTitle,
        wall_description: wallDescription,
        library_id: libraryId,
        ...postPosition,
      }),
      ...fetchOptions,
    })
  }

  public static async approve({ wishId }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.approveUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({}),
      ...fetchOptions,
    })
  }

  public static async reject({ wishId }: { wishId: Id }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.rejectUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJsonWithRetries(`${this.url}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }
}

class WishDraft extends FetchableObject {
  public static get url(): string {
    return '/api/1/wish-drafts'
  }

  public static async list({ wallId }: { wallId: WallType['id'] }): Promise<DraftPost[]> {
    const response = await fetchJson(this.url, {
      method: HTTPMethod.get,
      query: {
        wallId: String(wallId),
      },
    })
    return response.data.map((d: any) => d.attributes)
  }

  public static async create(attributes: DraftPost): Promise<DraftPost> {
    const response = await fetchJson(this.url, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ attributes }),
    })
    return response.data.attributes
  }

  public static async update(draftId: Id, attributes: DraftPost): Promise<DraftPost> {
    const response = await fetchJson(`${this.url}/${draftId}`, {
      method: HTTPMethod.patch,
      body: asciiSafeStringify({ attributes }),
    })
    return response.data.attributes
  }

  public static async delete(draftId: Id): Promise<void> {
    await fetchJson(`${this.url}/${draftId}`, {
      method: HTTPMethod.delete,
    })
  }
}

// UserContributor is read only
class UserContributor extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/users`
  }

  // The API WallContributorIds but it only return post author ids to match old behaviour for now
  public static async readWallContributorIds(wall, fetchOptions = {}): Promise<any> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wall.id}/contributors`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async read(userId: number): Promise<JsonAPIResponse<UserType>> {
    return await fetchUser(userId)
  }
}

/**
 * @example PadletApi.ContributingStatus.update({wall_id: 1, status: 1})
 */
class ContributingStatus extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/contributing_status`
  }

  public static get pingUrl(): string {
    return `/api/${API_VERSION}/contributing_status/ping`
  }

  public static async ping(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.pingUrl}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }
}

class BrahmsToken extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/brahms_token`
  }

  public static async get(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }
}

class Plan {
  /**
   * Fetch list of available plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<PlanType>> {
    return await fetchJson(`/api/${API_VERSION}/plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

const collaboratorCreate = async (
  { wallId, userId, right }: { wallId: Id; userId: Id; right: WallAccessRight },
  fetchOptions = {},
): Promise<any> => {
  return await fetchJson(`/api/${API_VERSION}/wall_collaborators/create`, {
    method: HTTPMethod.post,
    body: asciiSafeStringify({
      wall_id: wallId,
      user_id: userId,
      right,
    }),
    ...fetchOptions,
  })
}

const collaboratorLeavePadlet = async (wallId: Id, fetchOptions = {}): Promise<any> => {
  return await fetchJson(`/api/${API_VERSION}/wall_collaborators/leave`, {
    method: HTTPMethod.post,
    body: asciiSafeStringify({
      wall_id: wallId,
    }),
    ...fetchOptions,
  })
}

class WallCollaborator extends FetchableObject {
  public static async search({
    searchQuery,
    wallId,
    limit = 5,
  }: {
    searchQuery: string
    wallId: WallId
    limit?: number
  }): Promise<JsonAPIResponse<UserGroupWallCollaborator | UserWallCollaborator>> {
    const url = transformCurrentUrl(
      {},
      {
        path: '/api/1/wall-collaborators/search',
        search: {
          q: searchQuery,
          wall_id: String(wallId),
          limit: String(limit),
        },
      },
    )
    return await fetchJson(url, {
      method: HTTPMethod.get,
    })
  }

  // Rate-limited: you can only send one invite per collaborator per minute
  // Returns 429 Too many requests if rate-limited
  public static async resendInvitation(privacyPolicyId: PrivacyPolicyId, collaboratorId: UserId): Promise<any> {
    const url = `/privacy_policies/${privacyPolicyId}/reinvite`
    return await fetchJson(url, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ invite_user_id: collaboratorId }),
    })
  }
}

class Library extends FetchableObject {
  public static get libraryUrl(): string {
    return `/api/1/libraries?userId=\${ userId }&filter=\${ filter }`
  }

  public static async fetchCreatableLibraries({ userId }, fetchOptions = {}): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'wall_creatable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchCreatableAndVisibleLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'wall_creatable_and_visible' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchViewableLibraries({ userId }, fetchOptions = {}): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'all_walls_viewable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchViewableAndVisibleLibraries(
    { userId }: { userId: UserId },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'all_walls_viewable_and_visible' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class Wallpaper extends FetchableObject {
  private static get backgroundsUrl(): string {
    return `/api/\${ apiVersion }/backgrounds/\${category}`
  }

  public static get routeMap(): Record<WallBackgroundType, string> {
    return {
      picture: 'pictures',
      solid_color: 'solid_colors',
      gradient: 'gradients',
      pattern: 'patterns',
      art_illustrations: 'art_illustrations',
    }
  }

  public static async fetchBackgrounds(
    { category }: { category: WallBackgroundType },
    fetchOptions = { apiVersion: 7 },
  ): Promise<WallBackground[]> {
    const { apiVersion, ...restFetchOptions } = fetchOptions
    const { data }: JsonAPIResponse<WallBackground> = await fetchJson(
      this.buildUrl(this.backgroundsUrl, { category: this.routeMap[category], apiVersion }),
      {
        method: HTTPMethod.get,
        ...restFetchOptions,
      },
    )

    if (data != null && Array.isArray(data)) {
      return data.map((jsonResource: JsonAPIResource<WallBackground>): WallBackground => jsonResource.attributes)
    } else {
      return []
    }
  }
}

class UserIntegrations extends FetchableObject {
  public static async fetchGoogleDriveAuthorization(
    id: UserId,
    fetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<GoogleDriveAuthorizationResult>> {
    const { apiVersion } = fetchOptions
    return await fetchJson(`/api/${apiVersion}/user/${id}/user_integrations/google_drive`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async refreshGoogleDriveAccessToken(
    id: UserId,
    fetchOptions = {},
  ): Promise<JsonAPIResponse<GoogleDriveAuthorizationResult>> {
    return await fetchJson(`/api/1/user/${id}/user_integrations/google_drive/refresh_access_token`, {
      method: HTTPMethod.post,
      ...fetchOptions,
    })
  }
}

class WallPrivacyOptions extends FetchableObject {
  public static get wallPrivacyOptionsUrl(): string {
    return `/api/\${ apiVersion }/surface/walls/\${ wallId }/privacy_options`
  }

  public static async fetchWallPrivacyOptions(
    wallId,
    fetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<CanSetWallPrivacyOptions>> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    return await fetchJson(this.buildUrl(this.wallPrivacyOptionsUrl, { wallId, apiVersion }), {
      method: HTTPMethod.get,
      ...restFetchOptions,
    })
  }
}

class WallPostProperties extends FetchableObject {
  public static get url(): string {
    return `/api/\${ apiVersion }/walls/\${ wallId }/post-properties`
  }

  public static async fetchWallPostProperties(
    wallId: number,
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<WallPostPropertiesType>> {
    const { apiVersion, ...otherFetchOptions } = fetchOptions
    return await fetchJson(this.buildUrl(this.url, { wallId, apiVersion }), {
      method: HTTPMethod.get,
      ...otherFetchOptions,
    })
  }
}

class MentionSuggestions extends FetchableObject {
  public static async fetchMentionSuggestions(
    params: { wallId: number; wishId?: number; q?: string },
    fetchOptions: VersionedFetchOptions & { url?: string } = { apiVersion: 1 },
  ): Promise<UserMentionSuggestion[]> {
    const { url, apiVersion, ...otherFetchOptions } = fetchOptions
    const query: FetchOptions['query'] = {
      wallId: params.wallId.toString(),
    }
    if (params.wishId != null) query.wishId = params.wishId.toString()
    if (params.q != null) query.q = params.q
    const response: JsonAPIResponse<UserMentionSuggestionAttributes> = await fetchJson(
      url ?? this.buildUrl(this.url, { apiVersion }),
      {
        ...otherFetchOptions,
        method: HTTPMethod.get,
        query,
      },
    )
    if (!Array.isArray(response.data)) {
      throw new Error('Mention suggestions should be an array')
    }
    const suggestions: UserMentionSuggestion[] = response.data.map(({ type, attributes }) => ({
      type,
      attributes,
    }))
    return suggestions
  }

  public static get url(): string {
    return `/api/\${ apiVersion }/mention_suggestions`
  }
}

class WallReadToken extends FetchableObject {
  public static get wallReadTokenUrl(): string {
    return `/padlets/\${ apiVersion }/\${ publicKey }/exports/read_token`
  }

  public static async fetchWallReadToken(
    publicKey,
    fetchOptions: VersionedFetchOptions = {},
  ): Promise<JsonAPIResponse<WallReadTokenResponse>> {
    const { apiVersion = 1, ...otherFetchOptions } = fetchOptions
    return await fetchJson(this.buildUrl(this.wallReadTokenUrl, { publicKey, apiVersion }), {
      method: HTTPMethod.get,
      ...otherFetchOptions,
    })
  }
}

class User extends FetchableObject {
  public static async fetchUserLibrarySettings(): Promise<JsonAPIResponse<UserLibrarySettingsApiResponse>> {
    return await fetchJson('/api/1/dashboard_settings/user_library_settings', {
      method: HTTPMethod.get,
    })
  }
}

class SectionBreakout extends FetchableObject {
  public static get sectionBreakoutUrl(): string {
    return `/api/\${ apiVersion }/walls/\${ wallId }/section-breakout/\${ method }`
  }

  public static async enable(
    wallId: WallId,
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<WallSectionBreakoutLink[]> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    const response = await fetchJson(this.buildUrl(this.sectionBreakoutUrl, { wallId, apiVersion, method: 'enable' }), {
      method: HTTPMethod.patch,
      ...restFetchOptions,
    })

    return response.data.map((d: JsonAPIResource<WallSectionBreakoutLink>): WallSectionBreakoutLink => d.attributes)
  }

  public static async list(
    wallId: WallId,
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<WallSectionBreakoutLink[]> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    const response = await fetchJson(this.buildUrl(this.sectionBreakoutUrl, { wallId, apiVersion, method: '' }), {
      method: HTTPMethod.get,
      ...restFetchOptions,
    })

    return response.data.map((d: JsonAPIResource<WallSectionBreakoutLink>): WallSectionBreakoutLink => d.attributes)
  }

  public static async disable(wallId: WallId, fetchOptions: VersionedFetchOptions = { apiVersion: 1 }): Promise<void> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    // using fetchResponse because api does not return json
    await fetchResponse(this.buildUrl(this.sectionBreakoutUrl, { wallId, apiVersion, method: 'disable' }), {
      method: HTTPMethod.patch,
      headers: {
        'X-CSRF-Token': getCsrfToken(),
      },
      ...restFetchOptions,
    })
  }
}

class SubmissionRequest extends FetchableObject {
  public static get submissionRequestUrl(): string {
    return `/api/\${ apiVersion }/walls/\${ wallId }/submission-request/\${ method }`
  }

  public static async enable(
    wallId: WallId,
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<WallSubmissionRequestLink> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    const response = await fetchJson(
      this.buildUrl(this.submissionRequestUrl, { wallId, apiVersion, method: 'enable' }),
      {
        method: HTTPMethod.patch,
        ...restFetchOptions,
      },
    )

    return response.data.attributes
  }

  public static async update(
    wallId: WallId,
    attributes: WallSubmissionRequestProps,
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<WallSubmissionRequestLink> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    const response = await fetchJson(this.buildUrl(this.submissionRequestUrl, { wallId, apiVersion, method: '' }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({ attributes }),
      ...restFetchOptions,
    })

    return response.data.attributes
  }

  public static async disable(wallId: WallId, fetchOptions: VersionedFetchOptions = { apiVersion: 1 }): Promise<void> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    // using fetchResponse because api does not return json
    await fetchResponse(this.buildUrl(this.submissionRequestUrl, { wallId, apiVersion, method: 'disable' }), {
      method: HTTPMethod.patch,
      headers: {
        'X-CSRF-Token': getCsrfToken(),
      },
      ...restFetchOptions,
    })
  }
}

export default {
  Reaction,
  PostConnection,
  Comment,
  CommentV2,
  WallAccessSettings,
  WallSection,
  WallTableLayout,
  Wall,
  Wish,
  WishDraft,
  UserContributor,
  ContributingStatus,
  BrahmsToken,
  Plan,
  collaboratorCreate,
  collaboratorLeavePadlet,
  Library,
  Wallpaper,
  WallPrivacyOptions,
  WallPostProperties,
  MentionSuggestions,
  UserIntegrations,
  Poll,
  WallReadToken,
  User,
  WallCollaborator,
  SectionBreakout,
  SubmissionRequest,
}
